import React from 'react';
import { render } from 'react-dom';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { AGGridProvider } from '@lwt-helix/ag-grid';
import App from './App';
import Login from './login/Login';
import { AG_GRID_LICENSE } from './common/components/table/agGrid/agGrid';
import { Routes } from './common/routes/routes';
import Auth from './login/Auth';
import { tokenTypes, unprotectedRoutes } from './constants/auth';
import { navigateToHome, navigateToLogin, navigateToPlatformHome } from './constants/nav';
import { useAuthStore } from './store/auth/store';
import Connect from './login/Connect';
import { appLabels, getDocumentTitle, getIsLoginPage, isAgentMetrics, runningApp } from './constants/app';
import { SOFTWARE_VERSION } from './constants/ver';
import { HelixThemeProvider } from '@helix/theming';
import '@helix/styles/dist/helix.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const { token, tokenType, platformServiceClient } = useAuthStore.getState();
const isLwaAuthenticated = token && platformServiceClient?.token === token;
const isKeycloakAuthenticated = token && tokenType === tokenTypes.KEYCLOAK;
const isAuthenticated = isLwaAuthenticated || isKeycloakAuthenticated;
const pathname = window.location.pathname;

const redirectAuth = () => {
    if (isAgentMetrics) return;
    useAuthStore.setState({ authError: undefined });

    if (!unprotectedRoutes.includes(pathname) && !isAuthenticated) {
        navigateToLogin();
    }

    if (isAuthenticated && isLoginPage) {
        const redirectPath = new URLSearchParams(window.location.search).get('redirect');
        if (redirectPath) {
            window.location.replace(redirectPath);
            return;
        }
    }
};

const isLoginPage = getIsLoginPage(window.location.pathname);
document.title = getDocumentTitle({
    appLabel: appLabels[runningApp],
    softwareVersion: SOFTWARE_VERSION,
    isLoginPage
});

const redirectAgentMetrics = () => {
    if (!isAgentMetrics) return;
    const isValidationRoute = pathname === Routes.VALIDATION.BASE;
    const isAuthRoute = pathname === Routes.AUTH.BASE;
    if (!isLwaAuthenticated && !isValidationRoute && !isAuthRoute) {
        navigateToPlatformHome();
        return;
    }

    const isLoginRoute = pathname === Routes.AUTH.LOG_IN;
    if (isLoginRoute) navigateToHome();
};

!isAgentMetrics && redirectAuth();
isAgentMetrics && redirectAgentMetrics();

const queryClient = new QueryClient();

const Root = () => {
    const token = useAuthStore(state => state.token);

    return (
        <Switch>
            {!isAgentMetrics && (
                <Route
                    exact
                    path={Routes.AUTH.CONNECT}
                >
                    <Connect />
                </Route>
            )}
            <Route
                exact
                path={Routes.AUTH.BASE}
            >
                <Auth />
            </Route>
            {token && <App />}
            {!isAgentMetrics && (
                <Route
                    exact
                    path={Routes.AUTH.LOG_IN}
                >
                    <Login />
                </Route>
            )}
        </Switch>
    );
};

render(
    <QueryClientProvider client={queryClient}>
        <HelixThemeProvider>
            <AGGridProvider license={AG_GRID_LICENSE}>
                <BrowserRouter>
                  <Root/>
                </BrowserRouter>
            </AGGridProvider>
        </HelixThemeProvider>
    </QueryClientProvider>,
    document.querySelector('#root')
);
